import {
    ANALYTICS_SITES,
    DEFAULT_RANGE_DAYS,
    formatShare,
    pageViewsPerVisitor,
    presetRange,
    RANGE_PRESETS,
    reportQuery,
    validateRange,
    withShares,
} from '../../lib/analytics.js';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html } from '../../lib/lit.js';

const CUSTOM = 'custom';

const NUMBER = new Intl.NumberFormat();
const RATIO = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

// The analytics dashboard: for a site d3-analytics counts, its visitors, page
// views and where visitors came from over a range of UTC days. The counts come
// from d3-analytics through the Staff API, which forwards the staff member's own
// token.
class AnalyticsView extends BaseComponent {
    static properties = {
        _site: { state: true },
        /** @type {number|'custom'} */
        _preset: { state: true },
        _customFrom: { state: true },
        _customTo: { state: true },
        /** @type {{from: string, to: string}|null} the range the report is for */
        _range: { state: true },
        _totals: { state: true },
        _sources: { state: true },
        _loading: { state: true },
        _error: { state: true },
    };

    static styles = css`
        :host {
            display: block;
        }
        .wrap {
            max-width: 60rem;
            margin: 0 auto;
            padding: 2rem 1rem 4rem;
        }
        .stat {
            font-size: 1.75rem;
            font-weight: 600;
            font-variant-numeric: tabular-nums;
        }
        table {
            font-size: 0.9rem;
        }
        td.num,
        th.num {
            text-align: end;
            font-variant-numeric: tabular-nums;
        }
        .share-bar {
            height: 0.375rem;
            background: var(--bs-primary);
            border-radius: 0.25rem;
        }
    `;

    constructor() {
        super();
        this._site = ANALYTICS_SITES[0];
        this._preset = DEFAULT_RANGE_DAYS;
        const initial = presetRange(DEFAULT_RANGE_DAYS, new Date());
        this._customFrom = initial.from;
        this._customTo = initial.to;
        this._range = initial;
        this._totals = null;
        this._sources = null;
        this._loading = false;
        this._error = '';
        // Each load gets a number, so a slow response for an old site or range
        // never overwrites the one the staff member is now looking at.
        this._loadSeq = 0;
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        const seq = ++this._loadSeq;
        const query = reportQuery(this._site, this._range);
        this._loading = true;
        this._error = '';
        try {
            const [totals, sources] = await Promise.all([
                api.get(`/analytics/totals${query}`),
                api.get(`/analytics/sources${query}`),
            ]);
            if (seq !== this._loadSeq) return;
            this._totals = totals;
            this._sources = sources.sources;
        } catch (error) {
            if (seq !== this._loadSeq) return;
            this._totals = null;
            this._sources = null;
            this._error = error.message;
        } finally {
            if (seq === this._loadSeq) this._loading = false;
        }
    }

    _pickSite(event) {
        this._site = event.target.value;
        this._load();
    }

    _pickPreset(preset) {
        this._preset = preset;
        if (preset === CUSTOM) {
            // Start the custom range from what's showing, so switching is a tweak.
            this._customFrom = this._range.from;
            this._customTo = this._range.to;
            return;
        }
        this._range = presetRange(preset, new Date());
        this._load();
    }

    _applyCustom(event) {
        event.preventDefault();
        if (validateRange(this._customFrom, this._customTo)) return;
        this._range = { from: this._customFrom, to: this._customTo };
        this._load();
    }

    render() {
        return html`
            <div class="wrap">
                <div class="d-flex align-items-center gap-2 mb-3">
                    <h1 class="h4 mb-0">Analytics</h1>
                    <button
                        class="btn btn-sm btn-outline-secondary"
                        type="button"
                        ?disabled=${this._loading}
                        @click=${this._load}
                    >
                        Refresh
                    </button>
                </div>
                ${this._renderControls()} ${this._renderBody()}
            </div>
        `;
    }

    _renderControls() {
        const customError =
            this._preset === CUSTOM ? validateRange(this._customFrom, this._customTo) : '';
        return html`
            <div class="d-flex flex-wrap align-items-end gap-3 mb-2">
                <div>
                    <label class="form-label small mb-1" for="site">Site</label>
                    <select id="site" class="form-select form-select-sm" @change=${this._pickSite}>
                        ${ANALYTICS_SITES.map(
                            (site) =>
                                html`<option value=${site} ?selected=${site === this._site}>${site}</option>`,
                        )}
                    </select>
                </div>
                <div>
                    <div class="form-label small mb-1">Range</div>
                    <div class="btn-group btn-group-sm" role="group" aria-label="Range">
                        ${RANGE_PRESETS.map(
                            (days) => html`<button
                                type="button"
                                class="btn ${this._preset === days ? 'btn-primary' : 'btn-outline-primary'}"
                                aria-pressed=${this._preset === days}
                                @click=${() => this._pickPreset(days)}
                            >
                                ${days} days
                            </button>`,
                        )}
                        <button
                            type="button"
                            class="btn ${this._preset === CUSTOM ? 'btn-primary' : 'btn-outline-primary'}"
                            aria-pressed=${this._preset === CUSTOM}
                            @click=${() => this._pickPreset(CUSTOM)}
                        >
                            Custom
                        </button>
                    </div>
                </div>
                ${
                    this._preset === CUSTOM
                        ? html`<form class="d-flex align-items-end gap-2" @submit=${this._applyCustom}>
                              <div>
                                  <label class="form-label small mb-1" for="from">From</label>
                                  <input
                                      id="from"
                                      type="date"
                                      class="form-control form-control-sm"
                                      .value=${this._customFrom}
                                      @input=${(e) => {
                                          this._customFrom = e.target.value;
                                      }}
                                  />
                              </div>
                              <div>
                                  <label class="form-label small mb-1" for="to">To</label>
                                  <input
                                      id="to"
                                      type="date"
                                      class="form-control form-control-sm"
                                      .value=${this._customTo}
                                      @input=${(e) => {
                                          this._customTo = e.target.value;
                                      }}
                                  />
                              </div>
                              <button class="btn btn-sm btn-primary" type="submit" ?disabled=${!!customError}>
                                  Show
                              </button>
                          </form>`
                        : ''
                }
            </div>
            ${customError ? html`<div class="text-danger small mb-2">${customError}</div>` : ''}
            <p class="text-muted small mb-4">
                ${
                    this._range.from === this._range.to
                        ? html`${this._range.from}`
                        : html`${this._range.from} to ${this._range.to}`
                }
                (UTC days, both included)
            </p>
        `;
    }

    _renderBody() {
        if (this._error) {
            return html`<div class="alert alert-danger" role="alert">${this._error}</div>`;
        }
        if (!this._totals) {
            return html`<p class="text-muted">Loading…</p>`;
        }
        if (this._totals.visitors === 0) {
            return html`<p class="text-muted">No visits to ${this._totals.site} in this range.</p>`;
        }
        return html`${this._renderTotals()} ${this._renderSources()}`;
    }

    _renderTotals() {
        const perVisitor = pageViewsPerVisitor(this._totals);
        return html`
            <div class="row g-3 mb-2">
                ${this._renderStat('Visitors', NUMBER.format(this._totals.visitors))}
                ${this._renderStat('Page views', NUMBER.format(this._totals.pageViews))}
                ${this._renderStat('Page views per visitor', perVisitor === null ? '—' : RATIO.format(perVisitor))}
            </div>
            <p class="text-muted small mb-4">
                A person counts once a day: someone who visits on three days of the range is three visitors.
            </p>
        `;
    }

    _renderStat(label, value) {
        return html`
            <div class="col-12 col-sm-4">
                <div class="card h-100">
                    <div class="card-body">
                        <div class="text-muted small">${label}</div>
                        <div class="stat">${value}</div>
                    </div>
                </div>
            </div>
        `;
    }

    _renderSources() {
        const sources = withShares(this._sources ?? []);
        return html`
            <h2 class="h6">Sources</h2>
            <p class="text-muted small">
                A visit's source is its UTM source, else the site that referred it, else “direct”. A person
                counts once a day, under the source of their first page view that day.
            </p>
            <div class="table-responsive">
                <table class="table table-sm align-middle">
                    <thead>
                        <tr>
                            <th>Source</th>
                            <th class="num">Visitors</th>
                            <th class="num">Share</th>
                            <th class="w-25"><span class="visually-hidden">Share bar</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        ${sources.map(
                            (s) => html`<tr>
                                <td>${s.source}</td>
                                <td class="num">${NUMBER.format(s.visitors)}</td>
                                <td class="num">${formatShare(s.share)}</td>
                                <td><div class="share-bar" style="width: ${(s.share * 100).toFixed(1)}%"></div></td>
                            </tr>`,
                        )}
                    </tbody>
                </table>
            </div>
        `;
    }
}

customElements.define('d3-analytics-view', AnalyticsView);
